import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Divider,
  Modal,
  Button,
} from '@weave-mui/material';
import { List, ListItem } from '@mui/material';
import LinearProgress, {
  linearProgressVariants,
} from '@weave-mui/linear-progress';

/**
 * MoveTestCasesModal - Modal for moving test cases to a different project
 *
 * Supports both individual (single) and bulk move operations. When multiple test
 * cases are selected, displays the count and list of test cases being moved.
 *
 * @param {Object} props - Component props
 * @param {boolean} [props.open=true] - Whether the modal is open
 * @param {string} props.sourceProject - Current project ID (projectname) from which test cases are being moved
 * @param {Array<{id: string, displayname: string}>} [props.projects=[]] - Available target projects
 * @param {Function} [props.onClose] - Callback when modal is closed
 * @param {Function} [props.onSubmit] - Callback when move is confirmed; receives targetProject ID
 * @param {boolean} [props.loading=false] - Loading state during move operation
 * @param {Array<string>} [props.selectedTestCases=[]] - Display names of test cases to move
 */
const MoveTestCasesModal = ({
  open = true,
  sourceProject,
  projects = [],
  onClose = () => {},
  onSubmit = () => {},
  loading = false,
  selectedTestCases = [],
}) => {
  const targetProjects = projects.filter((p) => p.id !== sourceProject);
  const [targetProject, setTargetProject] = useState(() =>
    targetProjects.length > 0 ? targetProjects[0].id : '',
  );
  const isSingle = selectedTestCases.length === 1;
  const countLabel = isSingle
    ? '1 test case selected'
    : `${selectedTestCases.length} test cases selected`;
  const title = isSingle ? 'Move test case' : 'Move test cases';
  const instructionText = isSingle
    ? 'Please select the project to which you want to move this test case.'
    : 'Please select the project to which you want to move these test cases.';

  const handleTargetProjectChange = useCallback((e) => {
    setTargetProject(e.target.value);
  }, []);

  const handleSubmit = useCallback(() => {
    onSubmit(targetProject);
  }, [onSubmit, targetProject]);

  const submitEnabled =
    targetProjects.length > 0 &&
    !!targetProject &&
    targetProject !== sourceProject;

  return (
    <Modal
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      header={title}
      actions={
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!submitEnabled || loading}
          >
            Move
          </Button>
        </Box>
      }
      sx={{
        '& .MuiModalWeave-content': {
          padding: '16px 24px !important',
        },
        '& .MuiModalWeave-child': {
          padding: '0px !important',
        },
        '& .MuiModalWeave-box': {
          width: '720px !important',
          maxWidth: '90vw',
        },
        '& .MuiModalWeave-actions': {
          padding: '0px 32px 16px 32px',
        },
        '& .MuiModalWeave-child.MuiBox-root': {
          padding: '16px 32px',
        },
      }}
    >
      <Box sx={{ px: 0, py: 0 }}>
        {loading && (
          <LinearProgress
            variant={linearProgressVariants.INDETERMINATE}
            aria-label="Moving test cases progress"
            sx={{ mb: 2 }}
          />
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {instructionText}
        </Typography>

        <Typography
          variant="caption"
          sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}
        >
          Source project
        </Typography>
        <Typography
          variant="body2"
          sx={{
            mb: 2,
            px: 1.5,
            py: 1,
            borderRadius: 1,
            backgroundColor: 'action.hover',
          }}
        >
          {projects.find((p) => p.id === sourceProject)?.displayname ||
            sourceProject}
        </Typography>

        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <Typography
            component="label"
            variant="caption"
            sx={{ mb: 1, display: 'block', fontWeight: 600 }}
            htmlFor="move-target-project"
          >
            Target project
          </Typography>
          <Select
            id="move-target-project"
            value={targetProject}
            onChange={handleTargetProjectChange}
            displayEmpty
            variant="outlined"
            MenuProps={{
              // constraint the menu height to 200px and stick to the bottom of the dropdown
              disableScrollLock: true,
              anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
              transformOrigin: { vertical: 'top', horizontal: 'left' },
              PaperProps: {
                sx: {
                  maxHeight: 200,
                  overflow: 'hidden',
                  '& ul': {
                    maxHeight: 200,
                    overflowY: 'auto',
                  },
                },
              },
            }}
            sx={{
              '& .MuiSelect-select': {
                height: '40px',
                minHeight: '40px',
                display: 'flex',
                alignItems: 'center',
              },
            }}
          >
            {targetProjects.length === 0 ? (
              <MenuItem value="" disabled>
                No other projects available
              </MenuItem>
            ) : (
              targetProjects.map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.displayname}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>

        <Divider sx={{ my: 2 }} />

        <Typography
          variant="subtitle2"
          color="text.primary"
          sx={{ mb: 1.5, fontWeight: 600 }}
        >
          {countLabel}
        </Typography>

        <List sx={{ mb: 0, px: 0, maxHeight: 200, overflowY: 'auto' }}>
          {selectedTestCases.map((testCaseName, index) => (
            <ListItem
              key={`${testCaseName}-${index}`}
              disableGutters
              sx={{
                mb: 1,
                px: 2,
                py: 1.5,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor: (theme) => theme.palette.action.hover,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}
              >
                {testCaseName}
              </Typography>
            </ListItem>
          ))}
        </List>
      </Box>
    </Modal>
  );
};

export default MoveTestCasesModal;
